import React, { useCallback, useMemo, useState } from 'react'
import { debounce, times } from 'lodash-es'
import { intl } from 'lang/intl'
import { InView } from 'react-intersection-observer'
import { FormattedMessage } from 'react-intl'

import {
  TtAutoGrid,
  TtButton,
  TtFilter,
  TtMenuItem,
  TtSearchFilter,
  TtVideoSelector,
} from '@23/react'

import { useModal } from '../../../Layout/Modal'
import { useCategoryListQuery } from '../../blocs/useCategory'
import { useVideoSearchInfiniteQuery } from '../../blocs/useVideo'

import { VideoCard } from './VideoCard/VideoPickerCard'

import './VideoPicker.scss'

interface VideoPickerProps {
  initialVideoId: number | null
  onSelect: (videoId: number | null) => void
}

const VideoPicker = ({ initialVideoId, onSelect }: VideoPickerProps) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [category, setCategory] = useState('all')
  const { data: categories } = useCategoryListQuery()
  const setSearchDebounced = useMemo(() => debounce(setSearchTerm, 300), [])

  const {
    data: searchedVideos,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useVideoSearchInfiniteQuery({
    search: searchTerm,
    category: category === 'all' ? undefined : +category,
    includeUnpublished: true,
  })
  const videos = useMemo(
    () => searchedVideos?.pages.flatMap((page) => page.videoIds) ?? [],
    [searchedVideos]
  )

  const [selectedVideo, setSelectedVideo] = useState<number | null>(
    initialVideoId
  )

  const handleSelection = useCallback((newSelection: string) => {
    const selection = newSelection.split(',').map((s) => +s)
    const lastSelected = selection.at(-1)
    if (lastSelected && !Number.isNaN(lastSelected)) {
      setSelectedVideo(lastSelected)
    } else {
      setSelectedVideo(null)
    }
  }, [])

  const showInitial =
    searchTerm === '' && category === 'all' && !!initialVideoId

  return (
    <>
      <TtSearchFilter
        className="video-picker-search-filter"
        role="search"
        onTt-change={(e) => {
          setSearchDebounced(e.detail.search)
        }}
      >
        <TtFilter
          name="category"
          label={intl.formatMessage({ defaultMessage: 'Category' })}
          className="spot-video-picker-category-filter"
          onTt-change={(e) => setCategory(e.detail.value)}
        >
          <TtMenuItem value="all" checked={category === 'all'}>
            <FormattedMessage defaultMessage="All videos" />
          </TtMenuItem>
          {categories?.data.map((c) => (
            <TtMenuItem key={c.id} value={`${c.id}`}>
              {c.title}
            </TtMenuItem>
          ))}
        </TtFilter>
      </TtSearchFilter>
      <output>
        {isLoading ? (
          <TtAutoGrid gridGap="md" min-item-width="225px">
            {times(60, (i) => (
              <VideoCard key={i} />
            ))}
          </TtAutoGrid>
        ) : (
          <TtVideoSelector
            selected={`${selectedVideo ?? ''}`}
            onTt-change={(event) =>
              handleSelection(event.detail.selectedVideos)
            }
          >
            {showInitial && (
              <VideoCard
                id={initialVideoId}
                selectable
                selected={initialVideoId === selectedVideo}
              />
            )}
            {showInitial
              ? videos
                  .filter((vid) => vid !== initialVideoId)
                  .map((videoId) => (
                    <VideoCard
                      key={videoId}
                      id={videoId}
                      selectable
                      selected={videoId === selectedVideo}
                    />
                  ))
              : videos.map((videoId) => (
                  <VideoCard
                    key={videoId}
                    id={videoId}
                    selectable
                    selected={videoId === selectedVideo}
                  />
                ))}
            {hasNextPage && (
              <>
                <InView
                  onChange={(inView) => {
                    if (inView && !isFetchingNextPage) fetchNextPage()
                  }}
                >
                  <VideoCard />
                </InView>
                {times(59 - (showInitial ? 1 : 0), (i) => (
                  <VideoCard key={i + videos.length} />
                ))}
              </>
            )}
          </TtVideoSelector>
        )}
      </output>
      <TtButton
        className="video-picker-confirm-button"
        slot="footer"
        disabled={!selectedVideo}
        pill
        onClick={() => onSelect(selectedVideo)}
      >
        {initialVideoId ? (
          <FormattedMessage defaultMessage="Replace video" />
        ) : (
          <FormattedMessage defaultMessage="Select video" />
        )}
      </TtButton>
    </>
  )
}

const useVideoPicker = () => {
  const { openModal, closeModal } = useModal()

  return useCallback(
    (initialSelection?: number) => {
      return new Promise<number | null>((resolve) => {
        openModal({
          label: 'Select a video',
          closable: true,
          className: 'video-picker',
          body: (
            <VideoPicker
              onSelect={(videoId) => {
                closeModal()
                resolve(videoId)
              }}
              initialVideoId={initialSelection ?? null}
            />
          ),
          onClose: () => resolve(null),
        })
      })
    },
    [openModal, closeModal]
  )
}

export { useVideoPicker }
